import type { App } from "vue";

import { handleError } from "@/error/errorHandler";
import { captureMessage } from "@/error/sentry";
import { useConnectionStore } from "@/store/connection";

export function initErrorListeners(app: App) {
  window.onunhandledrejection = (event) => {
    event.preventDefault();
    handleError(event.reason ?? new Error("Unhandled rejection with value " + event.reason));
  };
  window.addEventListener(
    "error",
    (event) => {
      // typing is wrong: event: Event | ErrorEvent, see https://github.com/microsoft/TypeScript/issues/62075
      if (event instanceof ErrorEvent) {
        handleError(event.error ?? new Error(event.message));
      } else {
        // loading error
        const el = (event as Event).target as unknown as ResourceElement;
        const url = el.src ?? el.href ?? "";
        if (!isPreload(el) && !isExtension(url) && !isIgnored(el)) {
          captureMessage(`Resource error in ${el.tagName}`, { info: { url } });
        }
      }

      function isPreload(el: ResourceElement) {
        // Vite's JS preload hints (modulepreload / preload as="script") are refetched
        // by the actual import(), and genuine failures surface via vite:preloadError instead.
        return el.rel === "modulepreload" || (el.rel === "preload" && el.as === "script");
      }

      function isExtension(url: string) {
        return /^(moz-extension|chrome-extension|safari-web-extension):/.test(url);
      }

      function isIgnored(el: ResourceElement) {
        return "ignoreError" in el.dataset;
      }
    },
    true,
  );
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    handleError(event.payload);
  });
  app.config.errorHandler = (err) => {
    handleError(err);
  };
  app.config.warnHandler = (err) => {
    handleError(err);
  };
}

interface ResourceElement extends HTMLElement {
  src?: string;
  href?: string;
  rel?: string;
  as?: string;
}

export function initConnectionListeners() {
  window.addEventListener("offline", () => {
    useConnectionStore().setOffline();
  });
  window.addEventListener("online", () => {
    useConnectionStore().setOnline();
  });
  document.addEventListener("freeze", () => {
    useConnectionStore().freeze();
  });
  document.addEventListener("resume", () => {
    useConnectionStore().unfreeze();
  });
}
